import { authHeader, getToken, clearToken } from "./auth";

const BASE = process.env.NEXT_PUBLIC_API_URL ?? "http://localhost:8000";

async function request<T>(path: string, init?: RequestInit): Promise<T> {
  const res = await fetch(`${BASE}${path}`, {
    ...init,
    headers: { "Content-Type": "application/json", ...authHeader(), ...init?.headers },
  });
  if (!res.ok) {
    // An expired/invalid JWT on an authenticated request: drop the dead token
    // and bounce to login so the user isn't left staring at a cryptic error.
    // Guarded by getToken() so a 401 on the login form (wrong password, no
    // token yet) keeps its error message instead of redirecting.
    if (res.status === 401 && getToken() && typeof window !== "undefined") {
      clearToken();
      window.location.href = "/login";
    }
    const body = await res.text();
    // Sürümlü hukuki onay kapısı: backend, metin sürümü artınca öğrenciye dönük
    // uçlara 403 + `consent_required` döner. Kullanıcı hangi sayfada olursa olsun
    // onay ekranına alınır — kapı frontend'de değil backend'de, bu yalnızca
    // kullanıcıyı çıkmaza düşürmemek için.
    if (res.status === 403 && typeof window !== "undefined"
        && body.includes("consent_required")
        && window.location.pathname !== "/consent") {
      window.location.href = "/consent";
    }
    throw new Error(body);
  }
  return res.json();
}

// Errors thrown by `request` carry the raw response body as their message. Our
// backend returns FastAPI `detail` either as a plain string or as a structured
// {code, message} object. This normalises both so callers can branch on `code`
// (e.g. "invalid_api_key") and always have a human-readable `message`.
export function parseApiError(e: unknown): { code: string; message: string } {
  const raw = e instanceof Error ? e.message : String(e ?? "");
  try {
    const parsed = JSON.parse(raw);
    const detail = parsed?.detail;
    if (detail && typeof detail === "object") {
      return { code: detail.code ?? "", message: detail.message ?? raw };
    }
    return { code: "", message: typeof detail === "string" ? detail : raw };
  } catch {
    return { code: "", message: raw };
  }
}

export type Case = { id: number; name: string; age: number; presenting_complaint: string; presenting_complaint_tr?: string | null; difficulty_level: string; is_custom?: boolean };
export type CueItem = { cue: string; significance: string; easy_to_miss_because: string };
export type CreateCaseRequest = {
  name: string; age: number; presenting_complaint: string;
  difficulty_level: string; default_mode: string;
  surface_presentation?: string; underlying_issues?: string;
  personality_and_voice?: string; silence_behavior?: string;
  opens_up_when?: string[]; shuts_down_when?: string[]; tests_therapist_by?: string[];
  cue_inventory?: CueItem[];
};
// "hint" = a mid-session supervisor whisper the student requested; it lives in the
// transcript so its position is preserved, but it is never patient/student speech.
export type TranscriptMessage = { role: "user" | "assistant" | "hint"; content: string; timestamp: string };
// Supervisor evaluation runs server-side in the background. "none" = never started
// (or the server restarted mid-run) — the caller should (re-)POST /evaluate.
// "too_short" = the session carried too few therapist turns to supervise; terminal,
// retrying never helps.
export type EvaluationStatus = { status: "ready" | "running" | "failed" | "none" | "too_short" };
// Supervisor reports the student has never opened, grouped per patient case, so the
// dashboard can badge every card from one request. Only sessions with a written
// report appear here (too-short / failed evaluations are not "unread").
export type UnreadReports = {
  total: number;
  cases: { patient_case_id: number; unread: number; latest_session_id: number }[];
};
export type Session = { id: number; patient_case_id: number; started_at: string; ended_at: string | null; supervisor_report: unknown; reset_cycle: number; transcript?: TranscriptMessage[] | null; notes?: string | null; hint_limit?: number | null; hint_upgrade_available?: boolean | null };
export type HintResponse = { role: "hint"; content: string; timestamp: string; hints_used: number; hint_limit: number };
export type TokenResponse = { access_token: string; user_id: number; name: string };
export type SubscriptionStatus = {
  tier: "free" | "basic" | "pro";
  sessions_used_this_month: number | null;
  sessions_limit: number | null;
};
export type Currency = "EUR" | "USD" | "TRY";
export type BillingInterval = "monthly" | "yearly";
// One plan in the catalog. Amounts are MINOR units of `currency` (cent / kuruş)
// and come from the real per-currency price list — never from a conversion.
// `yearly_price` is null when the plan has no annual price at all.
export type Plan = {
  name: string;
  currency: Currency;
  monthly_price: number;
  yearly_price: number | null;
  limits: Record<string, number | boolean>;
};
// The catalog is answered in ONE currency, the one the account country implies:
// TR accounts get the TRY list (amounts KDV included), everyone else gets EUR.
export type PlanCatalog = {
  currency: Currency;
  country_code: string;
  country_source: string;
  country_locked: boolean;
  is_domestic: boolean;
  tax_included: boolean;
  vat_rate: number;
  plans: Plan[];
};
// The account country itself: the anchor the price hangs on. Locked after the
// first payment, after which only an admin can move it.
export type AccountCountry = {
  country_code: string;
  source: string;
  locked: boolean;
  currency: Currency;
  is_domestic: boolean;
  vat_rate: number;
  tax_included: boolean;
};
// iyzico checkout (shadow). When the integration is off, `enabled` is false and
// the pricing page keeps its "coming soon" behaviour.
export type CheckoutResponse = {
  enabled: boolean;
  detail?: string;
  token?: string;
  checkoutFormContent?: string;
  tokenExpireTime?: number;
  currency?: Currency;
  amount_minor?: number;
};
export type CheckoutResult = {
  enabled: boolean;
  status?: string;
  tier?: string | null;
  current_period_end?: string | null;
  detail?: string;
};
// Invoice/billing details captured on the payment screen. `country_code` (ISO-2)
// is what tells a domestic (TR) sale from a tax-exempt export.
export type BillingProfile = {
  buyer_type: "individual" | "company";
  full_name: string;
  company_name: string;
  country: string;
  country_code: string;
  city: string;
  address: string;
  zip_code: string;
  phone: string;
  identity_number: string;
  tax_office: string;
  tax_id: string;
  email: string;
};
export type SkillKey = "empathy" | "reflection" | "question_quality" | "focus" | "rapport";
export type ProgressPoint = {
  session_id: number;
  case_id: number;
  case_name: string;
  ended_at: string | null;
  overall: number;
  skills: Partial<Record<SkillKey, number>>;
};
export type PatientProgress = {
  case_id: number;
  name: string;
  age: number;
  alliance: number;
  session_count: number;
  insights: number;
  progress_map: Record<string, { stage: string; labels: Record<string, string> }>;
  wellbeing: number | null;
  wellbeing_trend: { value: number; ended_at: string | null }[];
};
export type Progress = {
  overall: {
    trend: ProgressPoint[];
    early_avg: Partial<Record<SkillKey, number>> | null;
    recent_avg: Partial<Record<SkillKey, number>> | null;
    latest_overall: number | null;
    band: "beginning" | "developing" | "competent" | "strong" | "exceptional" | null;
  };
  stats: {
    evaluated_sessions: number;
    patients_count: number;
    practice_minutes: number;
    best_skill: SkillKey | null;
    weakest_skill: SkillKey | null;
  };
  patients: PatientProgress[];
};
export type CompetencyBand = "beginning" | "developing" | "competent" | "strong" | "exceptional";
// ── Case Card (Vaka Kartı) ───────────────────────────────────────────────────
// The shareable summary of the work with ONE patient. `payload` is the whole card:
// the public page renders exactly this object, frozen at share time, so later
// sessions never leak through an old link. It never carries transcript or
// supervisor prose — only counts, scores and the alliance arc.
export type CardPayload = {
  version: number;
  patient: { name: string; age: number };
  sessions: number;
  evaluated_sessions: number;
  skills: Partial<Record<SkillKey, number>>;
  overall: number | null;
  band: CompetencyBand | null;
  most_improved: { skill: SkillKey; delta: number } | null;
  alliance: { start: number; current: number; delta: number; trail: number[] };
  student_name: string | null;
  locale: string;
  generated_at: string;
};
export type CardShare = {
  id: number;
  slug: string;
  url: string;
  locale: string;
  show_name: boolean;
  patient_case_id: number;
  created_at: string;
  payload: CardPayload;
};
export type CardPreview = { payload: CardPayload; share: CardShare | null };
export type PublicCard = { slug: string; locale: string; created_at: string; payload: CardPayload };

// Public card fetch used by the server-rendered /card/[slug] page: no token, no
// localStorage (which `request` reaches for), and never cached — a revoked card
// has to stop resolving immediately.
export async function fetchPublicCard(slug: string): Promise<PublicCard | null> {
  // On the server the public API hostname may not be reachable from inside the
  // container (it would have to hairpin through the proxy), so an optional
  // runtime-only API_INTERNAL_URL wins there. In the browser it is undefined and
  // the normal base is used.
  const base = process.env.API_INTERNAL_URL || BASE;
  try {
    const res = await fetch(`${base}/cards/public/${encodeURIComponent(slug)}`, { cache: "no-store" });
    if (!res.ok) return null;
    return (await res.json()) as PublicCard;
  } catch {
    return null;
  }
}

export type NotificationPrefs = { session_feedback: boolean; product_updates: boolean };
// "Invite a Friend" — the user's referral code + shareable link + reward stats.
export type Referral = {
  code: string;
  link: string;
  invited_count: number;
  rewarded_count: number;
  bonus_active: boolean;
  bonus_pro_until: string | null;
  reward_days: number;
};
// ── Kurumsal katman: kurum → kohort → koltuk ─────────────────────────────────
// The registration form has ONE code field; `lookupCohort` is what tells a cohort
// invite code from a referral code. `found: false` is a normal answer, not an error.
export type CohortLookup = {
  found: boolean;
  cohort_id?: number;
  code?: string;
  name?: string;
  organization_name?: string;
  instructor_name?: string | null;
  starts_at?: string | null;
  ends_at?: string | null;
  seat_limit?: number;
  seats_taken?: number;
  seats_available?: number | null;
  joinable?: boolean;
  reason?: "full" | "ended" | "inactive" | null;
};
export type CohortMembership = {
  member: boolean;
  active?: boolean;
  cohort_id?: number;
  cohort_name?: string;
  organization_name?: string;
  instructor_name?: string | null;
  joined_at?: string | null;
  ends_at?: string | null;
};
export type InstructorMe = {
  instructor: boolean;
  name?: string;
  cohort_id?: number;
  cohort_name?: string;
  open?: boolean;
};
export type InstructorStudentRow = {
  student_id: number;
  name: string;
  joined_at: string | null;
  left_at: string | null;
  status: string;
  sessions_count: number;
  evaluated_count: number;
  latest_overall: number | null;
  band: string | null;
  early_avg: number | null;
  recent_avg: number | null;
  last_session_at: string | null;
  started: boolean;
};
export type InstructorOverview = {
  cohort: {
    id: number; name: string; organization_name: string;
    starts_at: string | null; ends_at: string | null;
    seat_limit: number; seats_taken: number; seats_available: number | null;
  };
  stats: {
    students: number; not_started: number; total_sessions: number;
    evaluated_students: number; cohort_avg: number | null;
  };
};
export type InstructorStudentDetail = {
  student: { id: number; name: string; joined_at: string | null; status: string };
  window: { start: string | null; end: string | null };
  summary: {
    sessions_count: number; evaluated_count: number;
    latest_overall: number | null; band: string | null;
  };
  trend: { session_id: number; ended_at: string | null; overall: number; skills: Partial<Record<SkillKey, number>> }[];
  sessions: {
    session_id: number; case_name: string; started_at: string | null; ended_at: string | null;
    message_count: number; has_report: boolean; overall: number | null;
  }[];
  custom_cases: {
    id: number; name: string; age: number; presenting_complaint: string;
    difficulty_level: string; created_at: string | null;
  }[];
};
export type InstructorSessionDetail = {
  session_id: number;
  case_name: string;
  case_complaint: string | null;
  started_at: string | null;
  ended_at: string | null;
  transcript: TranscriptMessage[];
  supervisor_report: unknown;
};
export type InstructorInvitation = {
  valid: boolean;
  email?: string;
  cohort_name?: string;
  organization_name?: string;
  has_account?: boolean;
};

export type GuideSection = { heading: string; body: string; bullets: string[] };
export type GuideContent = { title: string; intro: string; sections: GuideSection[]; footer: string };
export type Guide = { locale: string; content: GuideContent; source: string; generated_at: string | null };

export const api = {
  register: (email: string, name: string, password: string, locale?: string, referral_code?: string) =>
    request<TokenResponse>("/auth/register", { method: "POST", body: JSON.stringify({ email, name, password, locale, referral_code }) }),

  changePassword: (current_password: string, new_password: string) =>
    request<{ ok: boolean }>("/auth/password", { method: "PUT", body: JSON.stringify({ current_password, new_password }) }),

  // `consent_accepted` = kullanıcı hukuki metinleri HİÇ kabul etti mi (eski alan).
  // Kapıyı belirleyen alan `consent_current`: kabul edilen sürüm (`consent_version`)
  // yürürlükteki `legal_version` kadar yeni mi. İkisi birlikte "metin güncellendi"
  // durumunu ayırt etmeyi sağlar (accepted && !current).
  getMe: () =>
    request<{ id: number; name: string; email: string; consent_accepted: boolean; consent_version: string | null; legal_version: string; consent_current: boolean; email_verified: boolean }>("/auth/me"),

  verifyEmail: (code: string) =>
    request<{ ok: boolean; already_verified?: boolean }>("/auth/verify-email", { method: "POST", body: JSON.stringify({ code }) }),

  // Maildeki tek-tık doğrulama linki. Public uç: oturum gerekmez (kullanıcı linki
  // başka bir cihazda açmış olabilir), token'ın kendisi imzalıdır.
  verifyEmailLink: (token: string) =>
    request<{ ok: boolean; already_verified?: boolean; email?: string }>("/auth/verify-email/link", { method: "POST", body: JSON.stringify({ token }) }),

  resendVerification: (locale?: string) =>
    request<{ ok: boolean; already_verified?: boolean }>("/auth/verify-email/resend", { method: "POST", body: JSON.stringify({ locale }) }),

  forgotPassword: (email: string, locale?: string) =>
    request<{ ok: boolean }>("/auth/password/forgot", { method: "POST", body: JSON.stringify({ email, locale }) }),

  resetPassword: (token: string, new_password: string) =>
    request<{ ok: boolean }>("/auth/password/reset", { method: "POST", body: JSON.stringify({ token, new_password }) }),

  acceptConsent: (locale?: string, marketing?: boolean) =>
    request<{ ok: boolean }>("/auth/consent/accept", { method: "POST", body: JSON.stringify({ locale, marketing }) }),

  // NOT: `declineConsent` kaldırıldı. Onay akışında reddetme yoktur ve reddetmek
  // hesabı silmez; kabul etmeyen kullanıcı onay ekranında kalır. Hesabını silmek
  // isteyen aşağıdaki mail kodlu silme akışını kullanır.

  requestAccountDeletion: (locale?: string) =>
    request<{ ok: boolean }>("/auth/account/delete/request", { method: "POST", body: JSON.stringify({ locale }) }),

  confirmAccountDeletion: (code: string) =>
    request<{ ok: boolean }>("/auth/account/delete/confirm", { method: "POST", body: JSON.stringify({ code }) }),

  login: (email: string, password: string) =>
    request<TokenResponse>("/auth/login", { method: "POST", body: JSON.stringify({ email, password }) }),

  googleAuth: (credential: string, referral_code?: string) =>
    request<TokenResponse>("/auth/google", { method: "POST", body: JSON.stringify({ credential, referral_code }) }),

  getReferral: () => request<Referral>("/referral"),

  getCases: () => request<Case[]>("/cases"),

  getGuide: (locale: string) => request<Guide>(`/guide?locale=${encodeURIComponent(locale)}`),

  startSession: (caseId: number) =>
    request<Session>("/sessions", { method: "POST", body: JSON.stringify({ patient_case_id: caseId }) }),

  sendMessage: (sessionId: number, content: string) =>
    request<{ role: string; content: string; timestamp: string; session_ended_by_patient: boolean; session_ended_by_limit: boolean; student_message_count: number; message_cap: number }>(`/sessions/${sessionId}/message`, {
      method: "POST",
      body: JSON.stringify({ content }),
    }),

  requestHint: (sessionId: number, locale = "en") =>
    request<HintResponse>(`/sessions/${sessionId}/hint?language=${encodeURIComponent(locale)}`, { method: "POST" }),

  endSession: (sessionId: number) =>
    request<Session>(`/sessions/${sessionId}/end`, { method: "POST" }),

  updateSessionNotes: (sessionId: number, content: string) =>
    request<{ ok: boolean }>(`/sessions/${sessionId}/notes`, {
      method: "PUT",
      body: JSON.stringify({ content }),
    }),

  getSession: (sessionId: number) =>
    request<Session>(`/sessions/${sessionId}`),

  // Kicks off the supervisor evaluation as a BACKGROUND job on the server and
  // returns immediately; poll getEvaluationStatus (or just re-call this — it's a
  // no-op while running) until "ready", then load the report via getSession.
  evaluateSession: (sessionId: number, locale = "en") =>
    request<EvaluationStatus>(`/sessions/${sessionId}/evaluate?language=${encodeURIComponent(locale)}`, { method: "POST" }),

  getEvaluationStatus: (sessionId: number) =>
    request<EvaluationStatus>(`/sessions/${sessionId}/evaluation-status`),

  getUnreadReports: () =>
    request<UnreadReports>("/sessions/unread-reports"),

  // Idempotent: called when the report is actually on screen, so a remount or a
  // second tab repeating it is a no-op server-side.
  markReportSeen: (sessionId: number) =>
    request<{ ok: boolean; marked: boolean }>(`/sessions/${sessionId}/report-seen`, { method: "POST" }),

  getCaseState: (caseId: number) =>
    request<{ session_count: number }>(`/cases/${caseId}/state`),

  resetCaseState: (caseId: number) =>
    request<{ ok: boolean }>(`/cases/${caseId}/reset-state`, { method: "POST" }),

  getSessionsByCase: (caseId: number) =>
    request<Session[]>(`/sessions/by-case/${caseId}`),

  getProgress: (lang?: string) =>
    request<Progress>(`/sessions/progress${lang ? `?lang=${encodeURIComponent(lang)}` : ""}`),

  getSubscriptionStatus: () =>
    request<SubscriptionStatus>("/subscription/status"),

  getPlans: () =>
    request<PlanCatalog>("/subscription/plans"),

  // The server decides the currency from the account country; `currency` is sent
  // only so an older payload shape stays valid, and is ignored server-side.
  startCheckout: (plan: string, currency: Currency = "EUR", interval: BillingInterval = "monthly") =>
    request<CheckoutResponse>("/payments/subscription/checkout", {
      method: "POST",
      body: JSON.stringify({ plan, currency, interval }),
    }),

  getCheckoutResult: (token: string) =>
    request<CheckoutResult>("/payments/subscription/checkout/result", {
      method: "POST",
      body: JSON.stringify({ token }),
    }),

  cancelSubscription: () =>
    request<{ enabled: boolean; status?: string }>("/payments/subscription/cancel", { method: "POST" }),

  getBillingProfile: () =>
    request<BillingProfile>("/payments/billing-profile"),

  saveBillingProfile: (profile: Partial<BillingProfile>) =>
    request<BillingProfile>("/payments/billing-profile", { method: "PUT", body: JSON.stringify(profile) }),

  getAccountCountry: () =>
    request<AccountCountry>("/payments/account-country"),

  saveAccountCountry: (country_code: string) =>
    request<AccountCountry>("/payments/account-country", {
      method: "PUT",
      body: JSON.stringify({ country_code }),
    }),

  createCase: (body: CreateCaseRequest) =>
    request<Case>("/cases", { method: "POST", body: JSON.stringify(body) }),

  // Live card numbers for the Share dialog. Nothing becomes public here — only
  // shareCard() creates a page.
  getCardPreview: (caseId: number, locale: string, showName: boolean) =>
    request<CardPreview>(`/cards/preview/${caseId}?locale=${encodeURIComponent(locale)}&show_name=${showName}`),

  shareCard: (patient_case_id: number, locale: string, show_name: boolean) =>
    request<CardShare>("/cards/share", {
      method: "POST",
      body: JSON.stringify({ patient_case_id, locale, show_name }),
    }),

  getCardShares: () => request<CardShare[]>("/cards/shares"),

  revokeCardShare: (shareId: number) =>
    request<{ ok: boolean }>(`/cards/shares/${shareId}`, { method: "DELETE" }),
  // ── Kohort (kurumsal katman) ───────────────────────────────────────────────
  lookupCohort: (code: string) =>
    request<CohortLookup>(`/cohorts/lookup?code=${encodeURIComponent(code)}`),

  getCohortPublic: (cohortId: number) =>
    request<CohortLookup>(`/cohorts/${cohortId}/public`),

  // Only ever called from the separate cohort-consent screen — the explicit
  // instructor-access consent is part of the request body, not an implicit side
  // effect of typing a code.
  joinCohort: (code: string, locale?: string) =>
    request<{ ok: boolean; already_member: boolean; membership: CohortMembership }>("/cohorts/join", {
      method: "POST",
      body: JSON.stringify({ code, accept_instructor_access: true, locale }),
    }),

  getMyCohort: () => request<CohortMembership>("/cohorts/me"),

  // ── Eğitmen alanı ──────────────────────────────────────────────────────────
  getInstructorMe: () => request<InstructorMe>("/instructor/me"),

  getInstructorOverview: () => request<InstructorOverview>("/instructor/overview"),

  getInstructorStudents: () =>
    request<{ students: InstructorStudentRow[] }>("/instructor/students"),

  getInstructorStudent: (studentId: number) =>
    request<InstructorStudentDetail>(`/instructor/students/${studentId}`),

  getInstructorSession: (studentId: number, sessionId: number) =>
    request<InstructorSessionDetail>(`/instructor/students/${studentId}/sessions/${sessionId}`),

  readInstructorInvitation: (token: string) =>
    request<InstructorInvitation>(`/instructor/invitation?token=${encodeURIComponent(token)}`),

  acceptInstructorInvitation: (token: string, password: string, name?: string) =>
    request<TokenResponse>("/instructor/invitation/accept", {
      method: "POST",
      body: JSON.stringify({ token, password, name }),
    }),

  getNotificationPrefs: () =>
    request<NotificationPrefs>("/notifications/preferences"),

  updateNotificationPrefs: (prefs: NotificationPrefs) =>
    request<NotificationPrefs>("/notifications/preferences", { method: "PUT", body: JSON.stringify(prefs) }),

  // ── Eğitmenin öğrenci davetleri ────────────────────────────────────────────
  // Davet KİŞİYE ÖZEL ve TEK KULLANIMLIKTIR; `link` her satırda döner çünkü mail
  // gitmediğinde eğitmenin linki elden iletebilmesi bilinçli bir karardır.
  getCohortInvitations: () =>
    request<CohortInvitationList>("/instructor/invitations"),

  // `locale` gönderilmezse davet maili İngilizce gider — arayüz dilini taşımak
  // bu yüzden opsiyonel değil, pratikte zorunlu.
  sendCohortInvitations: (emails: string[], locale?: string) =>
    request<CohortInviteSendResponse>("/instructor/invitations", {
      method: "POST",
      body: JSON.stringify({ emails, locale }),
    }),

  // Yeniden gönderim YENİ token üretir, eskisini geçersiz kılar. Kabul edilmiş
  // davette 409 + `already_accepted` döner.
  resendCohortInvitation: (invitationId: number, locale?: string) =>
    request<{ ok: boolean; email_sent: boolean; link: string }>(
      `/instructor/invitations/${invitationId}/resend`,
      { method: "POST", body: JSON.stringify({ locale }) },
    ),

  revokeCohortInvitation: (invitationId: number) =>
    request<{ ok: boolean }>(`/instructor/invitations/${invitationId}`, { method: "DELETE" }),

  removeCohortStudent: (studentId: number) =>
    request<{ ok: boolean }>(`/instructor/students/${studentId}`, { method: "DELETE" }),

  // Paylaşımlı (kişiye özel OLMAYAN) kohort linki — ikincil yol.
  getCohortShareLink: () => request<CohortShareLink>("/instructor/invite-link"),

  // Kayıt sayfasının kişiye özel daveti tanıması için public uç (auth yok).
  readCohortInviteToken: (token: string) =>
    request<CohortInviteToken>(`/cohorts/invitation?token=${encodeURIComponent(token)}`),

  // Aynı /cohorts/join ucu, kodun yerine tek kullanımlık token ile.
  joinCohortByInvite: (invite_token: string, locale?: string) =>
    request<{ ok: boolean; already_member: boolean; membership: CohortMembership }>("/cohorts/join", {
      method: "POST",
      body: JSON.stringify({ invite_token, accept_instructor_access: true, locale }),
    }),
};

// ── Eğitmen davet tipleri ────────────────────────────────────────────────────
export type CohortInviteStatus = "pending" | "accepted" | "revoked";
export type CohortInvitationRow = {
  id: number;
  email: string;
  status: CohortInviteStatus;
  email_sent: boolean;
  sent_at: string | null;
  accepted_at: string | null;
  accepted_name: string | null;
  link: string;
};
export type CohortInvitationList = {
  seat_limit: number;
  seats_taken: number;
  seats_available: number | null;
  invitations: CohortInvitationRow[];
};
// `resent` yalnızca "tekrar gönderildi" demek değildir: İPTAL EDİLMİŞ bir adres
// yeniden davet edilince de satır yeniden kullanılır ve sonuç `resent` olur.
export type CohortInviteOutcome =
  | "created" | "resent" | "invalid" | "duplicate_in_input" | "already_accepted";
export type CohortInviteResult = {
  email: string;
  outcome: CohortInviteOutcome;
  email_sent: boolean;
  link: string | null;
  id: number | null;
};
export type CohortInviteSendResponse = {
  seat_limit: number;
  seats_taken: number;
  seats_available: number | null;
  results: CohortInviteResult[];
};
export type CohortShareLink = { code: string; url: string };
// Token seviyesindeki hatalarda (`not_found`/`used`/`revoked`/`expired`) hem
// `cohort` hem `email` null gelir. Token geçerli ama kohort kapalıysa
// (`full`/`ended`/`inactive`) `valid: false` olmasına rağmen `cohort` DOLUDUR —
// davetli kişi neyi kaçırdığını görebilsin diye.
export type CohortInviteToken = {
  valid: boolean;
  reason: null | "not_found" | "used" | "revoked" | "expired" | "full" | "ended" | "inactive";
  email: string | null;
  cohort: {
    id: number;
    name: string;
    organization_name: string;
    instructor_name: string | null;
    seat_limit: number;
    seats_taken: number;
    seats_available: number | null;
    ends_at: string | null;
  } | null;
};
